import { useCallback, useMemo, useState } from 'react';

import type {
  Category,
  CustomFilterConfig,
  CustomFilterMatchValue,
  CustomFilterOption,
  FreeHotelItem,
} from 'hooks/useDirectoryData/types';

type FilterValues = Record<string, string[]>;

function toParts(value: CustomFilterMatchValue, delimiter: string) {
  return value
    .toString()
    .split(delimiter)
    .map((part) => {
      return part.trim().toLowerCase();
    })
    .filter(Boolean);
}

function valueMatches(raw: string, matchValue: CustomFilterMatchValue, delimiter?: string) {
  if (!delimiter) return raw === matchValue.toString();
  const itemParts = toParts(raw, delimiter);
  const matchParts = toParts(matchValue, delimiter);
  // Every part must be present so a matchValue that itself contains the delimiter (e.g. "History, Culture & Tours") still matches.
  return (
    matchParts.length > 0 &&
    matchParts.every((part) => {
      return itemParts.includes(part);
    })
  );
}

function optionMatches(item: FreeHotelItem, option: CustomFilterOption, delimiter?: string): boolean {
  const value = item[option.column];
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') return false;
  const raw = value.toString();
  return [option.matchValue].flat().some((matchValue) => {
    return valueMatches(raw, matchValue, delimiter);
  });
}

function matchesFilter(item: FreeHotelItem, filter: CustomFilterConfig, selected: string[]): boolean {
  if (selected.length === 0) return true;
  const active = filter.options.filter((option) => {
    return selected.includes(option.value);
  });
  const checks = active.map((option) => {
    return optionMatches(item, option, filter.delimiter);
  });
  return filter.matchAny ? checks.some(Boolean) : checks.every(Boolean);
}

function isOptionVisible(option: CustomFilterOption, categoryName: string | null, values: FilterValues): boolean {
  return Object.entries(option.when ?? {}).every(([key, allowed]) => {
    const selected = key === 'category' ? (categoryName ? [categoryName] : []) : (values[key] ?? []);
    return selected.some((value) => {
      return allowed.includes(value);
    });
  });
}

function pruneValues(
  filters: CustomFilterConfig[] | undefined,
  values: FilterValues,
  categoryName: string | null,
): FilterValues {
  if (!filters?.length) return values;
  const pruned: FilterValues = { ...values };
  filters.forEach((filter) => {
    const selected = values[filter.id];
    if (!selected) return;
    pruned[filter.id] = selected.filter((value) => {
      const option = filter.options.find((candidate) => {
        return candidate.value === value;
      });
      return option !== undefined && isOptionVisible(option, categoryName, values);
    });
  });
  return pruned;
}

export interface UseCustomFiltersResult {
  values: FilterValues;
  pendingValues: FilterValues;
  setFilterValue: (filterId: string, selected: string[]) => void;
  setPendingValue: (filterId: string, selected: string[]) => void;
  commitPending: () => void;
  resetAll: () => void;
  getVisibleFilters: (activeCategory: Category | null, displayValues: FilterValues) => CustomFilterConfig[];
  filteredFreeHotelsList: FreeHotelItem[];
}

export function useCustomFilters(
  filters: CustomFilterConfig[] | undefined,
  freeHotelsList: FreeHotelItem[],
  selectedCategory: Category | null,
): UseCustomFiltersResult {
  const categoryName = selectedCategory?.cat_name ?? null;
  const [values, setValues] = useState<FilterValues>({});
  const [pendingValues, setPendingValues] = useState<FilterValues>({});

  // Drop selections the new category hides, so they don't come back when switching back to the old one.
  const [prevCategoryName, setPrevCategoryName] = useState(categoryName);
  if (prevCategoryName !== categoryName) {
    setPrevCategoryName(categoryName);
    setValues((prev) => {
      return pruneValues(filters, prev, categoryName);
    });
  }

  const setFilterValue = useCallback((filterId: string, selected: string[]) => {
    setValues((prev) => {
      return { ...prev, [filterId]: selected };
    });
  }, []);

  const setPendingValue = useCallback((filterId: string, selected: string[]) => {
    setPendingValues((prev) => {
      return { ...prev, [filterId]: selected };
    });
  }, []);

  const commitPending = useCallback(() => {
    setValues((prev) => {
      return { ...prev, ...pendingValues };
    });
    setPendingValues({});
  }, [pendingValues]);

  const resetAll = useCallback(() => {
    setValues({});
    setPendingValues({});
  }, []);

  // Also covers options that depend on another custom filter, whose selection can change without a category change.
  const activeValues = useMemo(() => {
    return pruneValues(filters, values, categoryName);
  }, [filters, values, categoryName]);

  const filteredFreeHotelsList = useMemo(() => {
    if (!filters?.length) return freeHotelsList;
    return freeHotelsList.filter((item) => {
      return filters.every((filter) => {
        return matchesFilter(item, filter, activeValues[filter.id] ?? []);
      });
    });
  }, [filters, freeHotelsList, activeValues]);

  // Per hideEmptyOptions filter: the option values that match at least one listing passing every *other* filter.
  // Ignoring the filter's own selection keeps its sibling options visible after one is picked.
  const nonEmptyOptionValues = useMemo(() => {
    const result: Record<string, string[]> = {};
    filters?.forEach((filter) => {
      if (!filter.hideEmptyOptions) return;
      const candidates = freeHotelsList.filter((item) => {
        return filters.every((other) => {
          return other === filter || matchesFilter(item, other, activeValues[other.id] ?? []);
        });
      });
      result[filter.id] = filter.options
        .filter((option) => {
          return candidates.some((item) => {
            return optionMatches(item, option, filter.delimiter);
          });
        })
        .map((option) => {
          return option.value;
        });
    });
    return result;
  }, [filters, freeHotelsList, activeValues]);

  // activeCategory differs from the committed category while the mobile sheet holds a pending choice.
  const getVisibleFilters = useCallback(
    (activeCategory: Category | null, displayValues: FilterValues) => {
      const activeCategoryName = activeCategory?.cat_name ?? null;
      // The listings belong to the committed category, so they can't tell which options a pending one would leave empty.
      const canHideEmpty = activeCategoryName === categoryName;
      return (filters ?? [])
        .map((filter) => {
          const selected = displayValues[filter.id] ?? [];
          const nonEmpty = nonEmptyOptionValues[filter.id];
          const options = filter.options.filter((option) => {
            if (!isOptionVisible(option, activeCategoryName, displayValues)) return false;
            if (!nonEmpty || !canHideEmpty) return true;
            return nonEmpty.includes(option.value) || selected.includes(option.value);
          });
          return { ...filter, options };
        })
        .filter((filter) => {
          return filter.options.length > 0;
        });
    },
    [filters, categoryName, nonEmptyOptionValues],
  );

  return {
    values,
    pendingValues,
    setFilterValue,
    setPendingValue,
    commitPending,
    resetAll,
    getVisibleFilters,
    filteredFreeHotelsList,
  };
}
